import React, { useState, useEffect, useRef } from 'react';
import { ThemeProvider } from './theme';
import { Footer, Header, ThemeToggle } from './layout';
import './theme/globals.css';

/* ─── Table of Contents ─── */

interface TocEntry {
  id: string;
  label: string;
  level: number;
}

const tocEntries: TocEntry[] = [
  { id: 'the-shift', label: 'The Shift', level: 2 },
  { id: 'the-founding-moment', label: 'The Founding Moment', level: 2 },
  { id: 'our-commitments', label: 'Our Commitments', level: 2 },
  { id: 'compounding-learning', label: 'Compounding Learning', level: 2 },
  { id: 'conclusion', label: 'Conclusion', level: 2 },
];

function TableOfContents({ articleRef }: { articleRef: React.RefObject<HTMLDivElement> }) {
  const [activeId, setActiveId] = useState<string>('');
  const [tocStyle, setTocStyle] = useState<React.CSSProperties>({});
  
  const SCROLL_OFFSET = 128;

  useEffect(() => {
    const updatePosition = () => {
      if (!articleRef.current) return;
      
      const articleRect = articleRef.current.getBoundingClientRect();
      const scrollY = window.scrollY;
      const articleTop = articleRect.top + scrollY;
      const articleBottom = articleTop + articleRef.current.offsetHeight;
      const stickyTop = 128;
      const viewportTop = scrollY + stickyTop;
      
      const leftPos = articleRect.left - 200 - 32;
      
      if (leftPos < 8) {
        setTocStyle({ display: 'none' });
        return;
      }
      
      if (viewportTop < articleTop) {
        setTocStyle({
          position: 'absolute',
          top: 0,
          left: -232,
          width: 200,
        });
      } else if (viewportTop >= articleTop && viewportTop < articleBottom - 300) {
        setTocStyle({
          position: 'fixed',
          top: stickyTop,
          left: leftPos,
          width: 200,
        });
      } else {
        setTocStyle({
          position: 'absolute',
          bottom: 100,
          left: -232,
          width: 200,
        });
      }
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, { passive: true });
    window.addEventListener('resize', updatePosition, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [articleRef]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + SCROLL_OFFSET + 20;
      
      let currentActive = '';
      for (const entry of tocEntries) {
        const element = document.getElementById(entry.id);
        if (element) {
          const elementTop = element.getBoundingClientRect().top + window.scrollY;
          if (elementTop <= scrollPosition) {
            currentActive = entry.id;
          } else {
            break;
          }
        }
      }
      
      if (currentActive && currentActive !== activeId) {
        setActiveId(currentActive);
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeId]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const elementTop = element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: elementTop - SCROLL_OFFSET,
        behavior: 'smooth'
      });
      setActiveId(id);
    }
  };

  return (
    <nav className="hidden lg:block" style={tocStyle}>
      <ul className="space-y-2">
        {tocEntries.map((entry, index) => {
          const isActive = activeId === entry.id;
          return (
            <li
              key={index}
              className={`${entry.level === 3 ? 'pl-3' : ''}`}
            >
              <a
                href={`#${entry.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(entry.id);
                }}
                className={`block text-[14px] leading-[1.4] transition-colors ${
                  isActive 
                    ? 'text-foreground' 
                    : 'text-foreground-muted hover:text-foreground-secondary'
                }`}
              >
                {entry.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function ManifestoContent() {
  const articleRef = useRef<HTMLDivElement>(null);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <ThemeToggle />

      <main className="pt-28 sm:pt-36 pb-20 sm:pb-28">
        <div className="max-w-[660px] mx-auto px-6 sm:px-8">
          {/* Header */}
          <header className="text-center mb-10">
            <h1 className="font-display text-[24px] min-[480px]:text-[28px] sm:text-[32px] md:text-[36px] leading-[1.25] tracking-[-0.5px] font-medium text-foreground max-w-[600px] mx-auto mb-4">
              The End of Open-Loop AI
            </h1>
            <p className="text-[15px] text-foreground-muted italic mb-2">
              Sqwish Labs' founding manifesto
            </p>
            <p className="text-[13px] text-foreground-muted">
              March 28, 2026
            </p>
          </header>

          {/* Article body with relative positioning for TOC */}
          <div ref={articleRef} className="relative">
            {/* Table of Contents */}
            <TableOfContents articleRef={articleRef} />

            {/* Content */}
            <article className="prose-sqwish">
              <p className="text-foreground-secondary leading-[1.75] mb-6">
                The first generation of AI products will look strange in hindsight. We built systems that could reason, search, and act, then ran them in open loop: they produced outputs, but they did not learn from what happened next. We tuned them against benchmarks, froze them into prompts and workflows, and left humans to repair the gap between lab performance and reality.
              </p>
              <p className="text-foreground-secondary leading-[1.75] mb-6">
                You can see the failure everywhere. A support system gets better at answering and starts giving away refunds. A sales assistant lifts conversion and invents promises. A coding tool gets faster and less safe. Teams are working hard. Models are getting stronger. The failure comes from somewhere else: real AI products live inside conflicting objectives, while most deployed systems still assume the same setup should work for every request. A real product has to balance quality against cost, safety against helpfulness, speed against judgment. That balance moves from user to user and moment to moment.
              </p>
              <p className="text-foreground-secondary leading-[1.75] mb-6">
                That is why so many teams feel trapped in the manual grind. In our research with 127 engineers building LLM products in the UK, 92% said up to 40% of their time disappears into tuning loops. Some teams still optimize for public benchmarks. Better teams build their own internal eval suites. Both have value. Neither creates a learning loop. They tell you whether a system still passes yesterday's cases. They do not tell you what this request, from this user, under these constraints, should do right now.
              </p>
              <p className="text-foreground-secondary leading-[1.75] mb-6">
                The system does not learn. So the engineer becomes the learning system.
              </p>
              <p className="text-foreground-secondary leading-[1.75] mb-6">
                That gap is getting wider. AI products are moving into higher-stakes workflows, and the model landscape is getting more fragmented, not less. More models, more specialization, more churn. Static systems hit a ceiling in that world. Stuff more rules into a prompt and the behavior gets brittle. Push harder on fine-tuning and you buy capability at the cost of portability, clarity, and constant retraining. Tie yourself to one model and the next release turns months of work into debt. And foundation model providers cannot close this gap for you. They can improve the model. They cannot see your conversion events, your refund rates, your retention curve, or the moments where the output looked fine and the business outcome still got worse. Those signals live in the application layer, where the model providers are blind.
              </p>

              <h2 id="the-shift" className="text-[22px] sm:text-[26px] font-display font-medium text-foreground mt-12 mb-5 leading-[1.25] tracking-[-0.3px]">
                The Shift
              </h2>
              <p className="text-foreground-secondary leading-[1.75] mb-6">
                That is the shift that matters, and we are building for it. The future belongs to closed-loop AI: systems where the consequences of every decision feed back into the next one. Live product data and analytics stop being something humans inspect after the fact and start shaping what the system does next. The unit of optimization moves from the average workflow to the individual request. The question worth asking is whether a system can make the right trade-off for this case, right now, and learn from the result.
              </p>

              <h2 id="the-founding-moment" className="text-[22px] sm:text-[26px] font-display font-medium text-foreground mt-12 mb-5 leading-[1.25] tracking-[-0.3px]">
                The Founding Moment
              </h2>
              <p className="text-foreground-secondary leading-[1.75] mb-6">
                Sqwish Labs saw this earlier than most because we built the wrong thing first. Sqwish started as compression. The name came from squeezing context to make long-context systems cheaper and more accurate. The product improved benchmark performance. It also exposed the real problem. In some cases the system looked better in tests while customer satisfaction or instruction adherence got worse in production. We had gone looking for a local optimization and found out that local optimization was the wrong frame.
              </p>
              <p className="text-foreground-secondary leading-[1.75] mb-6">
                That was the founding moment. Compression was only the surface bug. The deeper failure was open-loop optimization: treating one part of the system as if it could be improved in isolation and then trusted inside the rest of the stack. But users never encounter prompt, retrieval, model choice, or tool use in isolation. They experience the downstream effect those choices create together. The whole system has to sit inside the learning loop. That was the moment Sqwish stopped being a tool and became a company.
              </p>

              <h2 id="our-commitments" className="text-[22px] sm:text-[26px] font-display font-medium text-foreground mt-12 mb-5 leading-[1.25] tracking-[-0.3px]">
                Our Commitments
              </h2>
              <p className="text-foreground-secondary leading-[1.75] mb-6">
                <strong className="text-foreground">We will keep real outcomes in the loop.</strong> Benchmarks, evals, latency charts, and cost dashboards matter, but they are instruments. We are building systems that learn from the results customers actually care about: resolution, retention, conversion, CSAT, safety, and cost under constraint. We will connect the system to the signals the business is already producing instead of pretending the benchmark is the business. If a metric matters in production, it belongs in the loop.
              </p>
              <p className="text-foreground-secondary leading-[1.75] mb-6">
                <strong className="text-foreground">We will optimize for moments, not averages.</strong> Most AI products are still built around an average case. Real value does not live there. It lives in the edge case, the high-stakes request, the subtle user, the moment where the trade-off changes. One-size-fits-all behavior is convenient for the builder and expensive for the customer. We are building systems that make a fresh decision for the request in front of them.
              </p>
              <p className="text-foreground-secondary leading-[1.75] mb-6">
                <strong className="text-foreground">We will build closed loops under guardrails.</strong> Learning in production is only useful if it stays worthy of trust. Serious customers need systems that can improve without gambling with their business. Cost, safety, latency, compliance, and brand constraints are not things to check after the system moves. They have to shape the move itself. We are not interested in a system that adapts by thrashing production and asking humans to clean up after it.
              </p>

              <h2 id="compounding-learning" className="text-[22px] sm:text-[26px] font-display font-medium text-foreground mt-12 mb-5 leading-[1.25] tracking-[-0.3px]">
                Compounding Learning
              </h2>
              <p className="text-foreground-secondary leading-[1.75] mb-6">
                So we are building learning that compounds across model generations. Intelligence should not be trapped inside one provider, one release cycle, or one brittle prompt stack. Every measured interaction becomes evidence for the next decision, and every model generation becomes new substrate rather than a forced reset. Customers should not have to rebuild their product every time the substrate shifts.
              </p>
              <p className="text-foreground-secondary leading-[1.75] mb-6">
                Sqwish is a research company because this is fundamentally a learning problem, and the team behind it has spent years on reinforcement learning in environments as unforgiving as jet engines and telecom networks, publishing more than 50 papers along the way. Early partners are already running live production traffic through the system on workflows where quality, cost, and safety pull against each other: exactly the kind of problem that static setups flatten.
              </p>

              <h2 id="conclusion" className="text-[22px] sm:text-[26px] font-display font-medium text-foreground mt-12 mb-5 leading-[1.25] tracking-[-0.3px]">
                Conclusion
              </h2>
              <p className="text-foreground-secondary leading-[1.75]">
                Open-loop AI got the world interested. Closed-loop AI will decide who lasts. We are building closed-loop AI: systems that learn from real outcomes, adapt to the moment, and compound under guardrails. That is what Sqwish is for.
              </p>
            </article>

            {/* CTA */}
            <div className="mt-16 pt-8 border-t border-border-subtle">
              <p className="text-foreground-secondary mb-4">
                Interested in our research?
              </p>
              <a
                href="/blog"
                className="text-small text-foreground-muted hover:text-foreground transition-colors"
              >
                Read our blog →
              </a>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export function ManifestoPage() {
  return (
    <ThemeProvider defaultTheme="light">
      <ManifestoContent />
    </ThemeProvider>
  );
}

export default ManifestoPage;
